/**
 * Admin information architecture — single source of truth.
 *
 * Each route has one clear job. Related routes link to each other; overlapping
 * concerns are spelled out so we don't edit the same thing in two places.
 */

export type AdminPageId =
  | "dashboard"
  | "centro"
  | "revenue"
  | "personal-finance"
  | "investor"
  | "country-templates"
  | "wishlist"
  | "cta"
  | "site-settings"
  | "portfolio"
  | "audiovisual"
  | "private-reels"
  | "productos"
  | "suscripciones"
  | "invitaciones"
  | "premium-invite"
  | "memberships"
  | "buyers"
  | "argen-links"
  | "live-funnel"
  | "checkout-funnel"
  | "recovery-leads"
  | "image-metrics"
  | "isp-report"
  | "kimi-patterns"
  | "integrations"
  | "config-health"
  | "worker-flags"
  | "env-setup"
  | "stripe-disputes"
  | "sidebar-config";

export type AdminPageMeta = {
  id: AdminPageId;
  /** Canonical path (no trailing slash). */
  path: string;
  /** Short nav label. */
  navLabel: string;
  /** Page H1. */
  title: string;
  /** One-line job description. */
  purpose: string;
  /** What this page owns (edit here only). */
  owns: string;
  /** Explicit non-overlap — where NOT to edit. */
  notHere?: string;
  /** Related paths for cross-links. */
  related?: Array<{ path: string; label: string }>;
};

export const ADMIN_PAGE_CATALOG: Record<AdminPageId, AdminPageMeta> = {
  dashboard: {
    id: "dashboard",
    path: "/admin",
    navLabel: "Dashboard",
    title: "Command center",
    purpose:
      "Pulso operativo: Stripe brand, compradores, pending, live, leads y fotos.",
    owns: "Overview en tiempo real (solo lectura) + deep-links.",
    notHere: "No se edita config aquí — usá Centro de control o la page dedicada.",
    related: [
      { path: "/admin/buyers", label: "Compradores" },
      { path: "/admin/memberships", label: "Aprobaciones" },
      { path: "/admin/kimi-patterns", label: "Offer engine" },
      { path: "/admin/live-funnel", label: "Live funnel" },
      { path: "/admin/revenue", label: "Revenue" },
    ],
  },
  centro: {
    id: "centro",
    path: "/admin/centro-de-control",
    navLabel: "Mapa de config",
    title: "Centro de control",
    purpose: "Mapa de qué se puede cambiar en runtime y a dónde ir a editarlo.",
    owns: "Navegación e índice de config (no edita datos).",
    notHere: "No duplica formularios — solo deep-links a la page dueña.",
    related: [
      { path: "/admin", label: "Dashboard" },
      { path: "/admin/country-templates", label: "Home por país" },
      { path: "/admin/premium-invite-config", label: "Invit. premium" },
    ],
  },
  revenue: {
    id: "revenue",
    path: "/admin/revenue",
    navLabel: "Revenue",
    title: "Revenue",
    purpose: "Ingresos de producto del sitio (Stripe brand + MP cobros brand).",
    owns: "Totales y gráficos de cobros exitosos del sitio.",
    notHere:
      "Wallet personal (Uber, QR, transferencias) → Finanzas personales. KPIs inversores → Investor KPI.",
    related: [
      { path: "/admin/finanzas-personales", label: "Finanzas personales" },
      { path: "/admin/investor-kpi", label: "KPIs Investors" },
      { path: "/admin/stripe-disputes", label: "Disputas" },
      { path: "/admin", label: "Dashboard" },
    ],
  },
  "personal-finance": {
    id: "personal-finance",
    path: "/admin/finanzas-personales",
    navLabel: "Finanzas personales",
    title: "Finanzas personales",
    purpose:
      "Ledger personal Oruon-compatible (mismo contrato que delfuocampo): items, loan, recurrentes, feed real MP/Stripe. DB personal-finance.",
    owns: "CRUD finanzas + API /api/finanzas/v1 + /api/admin/finanzas-personales.",
    notHere:
      "Revenue de producto del sitio → /admin/revenue. No mezclar con cobros brand/memberships.",
    related: [
      { path: "/admin/revenue", label: "Revenue producto" },
      { path: "/admin", label: "Dashboard" },
    ],
  },
  investor: {
    id: "investor",
    path: "/admin/investor-kpi",
    navLabel: "KPIs Investors",
    title: "KPIs Investors",
    purpose: "Métricas financieras / MRR / churn para reporting de inversores.",
    owns: "Serie investor-kpi y paneles asociados.",
    notHere: "Cash day-to-day → Revenue. Live de producto → Live funnel.",
    related: [{ path: "/admin/revenue", label: "Revenue" }],
  },
  "country-templates": {
    id: "country-templates",
    path: "/admin/country-templates",
    navLabel: "DualCTA Mux+Stripe",
    title: "DualCTA · Mux + Stripe por país",
    purpose:
      "Dueño de DualCTAVideoLanding: qué playback Mux ve el visitante y qué Stripe Price cobra el CTA Membresía Exclusiva, por ISO2. También aliases /admin/dual-cta y /admin/dual-cta-home.",
    owns:
      "site_settings.international_home_* (template DualCTAVideoLanding, mux_by_country A/B, vip_pricing stripePriceId+promo, dual_cta free TG, mux_by_ar_geo).",
    notHere:
      "Mux overlay global sin país → Invit. premium (solo fallback). Wishlist geo AR → CTA por región. Catálogo membership_products → Suscripciones (solo si no hay price_ país).",
    related: [
      { path: "/admin/dual-cta", label: "Atajo DualCTA" },
      { path: "/admin/premium-invite-config", label: "Fallbacks invite" },
      { path: "/admin/wishlist-config", label: "CTA por región" },
      { path: "/admin/suscripciones", label: "Catálogo VIP" },
    ],
  },
  wishlist: {
    id: "wishlist",
    path: "/admin/wishlist-config",
    navLabel: "CTA por región",
    title: "CTA por región",
    purpose: "Qué botón (wishlist / cumpleaños / oculto) se muestra por geo AR y países.",
    owns: "wishlist_geo_config (Worker + /api/wishlist-config).",
    notHere: "Home DualCTA / Mux / VIP price → Home por país. Overrides genéricos → CTAs.",
    related: [
      { path: "/admin/country-templates", label: "Home por país" },
      { path: "/admin/cta", label: "CTAs overrides" },
    ],
  },
  cta: {
    id: "cta",
    path: "/admin/cta",
    navLabel: "CTAs y overrides",
    title: "CTAs y overrides",
    purpose:
      "Overrides de infra/Worker (URL, toggle, precio display geo). Pipeline edge — no es cobro DualCTA.",
    owns: "cta_overrides + defaults env/Worker (KV version-gated).",
    notHere:
      "Cobro Stripe DualCTA (price_/promo_ país) → Home por país. Wishlist/cumpleaños → CTA por región. Mux home → Home por país.",
    related: [
      { path: "/admin/country-templates", label: "Home por país" },
      { path: "/admin/wishlist-config", label: "CTA por región" },
      { path: "/admin/worker-flags", label: "Worker" },
    ],
  },
  "site-settings": {
    id: "site-settings",
    path: "/admin/site-settings",
    navLabel: "Configuración",
    title: "Configuración del sitio",
    purpose: "Header, banner, logo, textos del shell BaseTemplate live.",
    owns: "site_settings (variant live) — branding y galería home clásica.",
    notHere: "Mux/template por país → Home por país. Portfolio secciones → Portfolio.",
    related: [
      { path: "/admin/portfolio", label: "Portfolio" },
      { path: "/admin/country-templates", label: "Home por país" },
    ],
  },
  portfolio: {
    id: "portfolio",
    path: "/admin/portfolio",
    navLabel: "Portfolio",
    title: "Portfolio",
    purpose: "Secciones e imágenes del portfolio (Cloudinary).",
    owns: "portfolio sections/images.",
    notHere: "Métricas de vistas de fotos → Métricas fotos.",
    related: [{ path: "/admin/image-metrics", label: "Métricas fotos" }],
  },
  audiovisual: {
    id: "audiovisual",
    path: "/admin/audiovisual",
    navLabel: "Audiovisual",
    title: "Audiovisual",
    purpose: "Videos de YouTube destacados en el sitio.",
    owns: "Tabla/API audiovisual.",
    related: [{ path: "/admin/site-settings", label: "Config sitio" }],
  },
  "private-reels": {
    id: "private-reels",
    path: "/admin/reels",
    navLabel: "Reels privados",
    title: "Reels privados (/reels)",
    purpose:
      "Hub de /reels con subpáginas: Videos, Acceso, Links multi-uso, Secret share. Meta fija creator_id=brand + external_id=feed.",
    owns:
      "private_reels_config + private_reels_share_links + reel_invitations + upload Mux. Filtro creator_id + external_id=feed.",
    notHere:
      "Layout Reels del embudo invitacion-premium (teaser/A-B) → Invit. premium. Mux home DualCTA por país → Home por país.",
    related: [
      { path: "/admin/reels/videos", label: "Videos / gestionar upload" },
      { path: "/admin/reels/acceso", label: "Acceso" },
      { path: "/admin/reels/links", label: "Links multi-uso" },
      { path: "/admin/reels/secret-share", label: "Secret share" },
      { path: "/admin/premium-invite-config", label: "Reels invite (otro pipeline)" },
    ],
  },
  productos: {
    id: "productos",
    path: "/admin/productos",
    navLabel: "Productos",
    title: "Productos",
    purpose: "Catálogo de packs one-time / productos individuales.",
    owns: "pack_config y productos admin.",
    notHere: "Planes recurrentes VIP → Suscripciones.",
    related: [{ path: "/admin/suscripciones", label: "Suscripciones" }],
  },
  suscripciones: {
    id: "suscripciones",
    path: "/admin/suscripciones",
    navLabel: "Suscripciones",
    title: "Suscripciones",
    purpose:
      "Catálogo membership_products (plan_tier → stripe_price_id). Solo se cobra si el país NO tiene price_ en Home por país.",
    owns: "membership_products / stripe plans admin (fallback VIP).",
    notHere:
      "VIP DualCTA por país (price_ + promo_ 1.er mes) → Home por país. No editar el mismo Price en dos lados: country gana.",
    related: [
      { path: "/admin/country-templates", label: "Home por país (gana)" },
      { path: "/admin/memberships", label: "Aprobaciones" },
    ],
  },
  invitaciones: {
    id: "invitaciones",
    path: "/admin/invitaciones",
    navLabel: "Códigos invite",
    title: "Códigos de invitación",
    purpose: "Generar códigos con tier, descuento y expiración.",
    owns: "invitation_codes.",
    notHere:
      "Ruta /invitacion-exclusive (Mux VIP) se abre desde AR BaseTemplate CTA community o link directo. Labels Mux → Invit. premium. Leads de pago → Leads recovery.",
    related: [
      { path: "/admin/wishlist-config", label: "CTA geo AR (Exclusivo)" },
      { path: "/admin/premium-invite-config", label: "Invit. premium" },
      { path: "/admin/recovery-leads", label: "Leads recovery" },
    ],
  },
  "premium-invite": {
    id: "premium-invite",
    path: "/admin/premium-invite-config",
    navLabel: "Invit. premium",
    title: "Invitación premium / free Telegram",
    purpose:
      "Labels CTA Mux, free TG, overlay global. NO es la plantilla home. AR exclusive URL = /invitacion-exclusive (wishlist community).",
    owns: "premium_invite_config (Postgres) — invite layouts + free URL + overlay global.",
    notHere:
      "Home DualCTA / VIP price → Home por país. CTA AR Exclusivo → CTA por región. Códigos one-time → Códigos invite.",
    related: [
      { path: "/admin/country-templates", label: "Home por país (DualCTA intl)" },
      { path: "/admin/wishlist-config", label: "CTA geo AR" },
      { path: "/admin/invitaciones", label: "Códigos invite" },
    ],
  },
  memberships: {
    id: "memberships",
    path: "/admin/memberships",
    navLabel: "Aprobaciones",
    title: "Aprobaciones de membresía",
    purpose:
      "Tras el pago (pending): aprobar y enviar email con URL free/privada (Telegram u otra https), no el checkout.",
    owns: "user_memberships approve + free access URL + email de acceso.",
    notHere:
      "Invite de campaña a /invitacion-exclusive → Compradores. Precios Stripe → Suscripciones.",
    related: [
      { path: "/admin/buyers", label: "Compradores" },
      { path: "/admin/suscripciones", label: "Suscripciones" },
      { path: "/admin", label: "Dashboard" },
    ],
  },
  buyers: {
    id: "buyers",
    path: "/admin/buyers",
    navLabel: "Compradores",
    title: "Compradores",
    purpose:
      "Clientes que pagaron este brand (user_memberships + Stripe Checkout live), oferta/LLM y reconcile → DB.",
    owns:
      "Lectura brand-scoped (metadata.source) + merge Stripe + invite personalizada + reconcile Stripe→membresías.",
    notHere:
      "Aprobar Telegram → Aprobaciones. Leads que no pagaron → Leads recovery. Códigos genéricos → Códigos invite.",
    related: [
      { path: "/admin/memberships", label: "Aprobaciones" },
      { path: "/admin/kimi-patterns", label: "Offer engine" },
      { path: "/admin/invitaciones", label: "Códigos invite" },
      { path: "/admin/recovery-leads", label: "Leads recovery" },
    ],
  },
  "argen-links": {
    id: "argen-links",
    path: "/admin/argen-links",
    navLabel: "Links Argentina",
    title: "Links Argentina",
    purpose: "Links compartibles AR (Stories / DM) hacia checkout AR.",
    owns: "Generación/copia de URLs AR.",
    notHere: "Config DualCTA país AR → Home por país.",
    related: [{ path: "/admin/country-templates", label: "Home por país" }],
  },
  "live-funnel": {
    id: "live-funnel",
    path: "/admin/live-funnel",
    navLabel: "Live funnel",
    title: "Live funnel",
    purpose: "Eventos y sesiones en vivo (últimos minutos).",
    owns: "Lectura de funnel_events en tiempo real.",
    notHere:
      "Agregados históricos de intents → Funnel checkout. Directorio de emails → Leads recovery.",
    related: [
      { path: "/admin/checkout-funnel", label: "Funnel checkout" },
      { path: "/admin/recovery-leads", label: "Leads recovery" },
      { path: "/admin", label: "Dashboard" },
    ],
  },
  "checkout-funnel": {
    id: "checkout-funnel",
    path: "/admin/checkout-funnel",
    navLabel: "Funnel checkout",
    title: "Funnel de checkout",
    purpose: "Intents started → completed → abandoned (agregado por template/plan).",
    owns: "Agregados de premium_checkout_intents + vistas.",
    notHere: "Feed en vivo → Live funnel. Lista exportable de emails → Leads recovery.",
    related: [
      { path: "/admin/live-funnel", label: "Live funnel" },
      { path: "/admin/recovery-leads", label: "Leads recovery" },
    ],
  },
  "recovery-leads": {
    id: "recovery-leads",
    path: "/admin/recovery-leads",
    navLabel: "Leads recovery",
    title: "Leads recovery",
    purpose:
      "Intentos de pago de esta brand (brand_source estricto). Emails reales, sin e2e/test. Completados → Compradores.",
    owns: "premium_checkout_intents directory + CSV + ventana recoverable 2–24h (este brand only).",
    notHere:
      "Pagaron y acceso free → Aprobaciones / Compradores. Stats por template → Funnel checkout. Stream → Live.",
    related: [
      { path: "/admin/buyers", label: "Compradores" },
      { path: "/admin/checkout-funnel", label: "Funnel checkout" },
      { path: "/admin/live-funnel", label: "Live funnel" },
    ],
  },
  "image-metrics": {
    id: "image-metrics",
    path: "/admin/image-metrics",
    navLabel: "Métricas fotos",
    title: "Métricas de fotos",
    purpose: "Dwell, países y engagement de lightbox (image_views).",
    owns: "Agregados image_views.",
    notHere: "Subir/editar fotos del portfolio → Portfolio.",
    related: [
      { path: "/admin/portfolio", label: "Portfolio" },
      { path: "/admin", label: "Dashboard" },
    ],
  },
  "isp-report": {
    id: "isp-report",
    path: "/admin/isp-report",
    navLabel: "Proveedores ISP",
    title: "Proveedores de internet",
    purpose: "Ranking ISP/ASN (IPLocate) de leads y CTAs.",
    owns: "visitor_ip_intel aggregates.",
    notHere: "Lista de emails de pago → Leads recovery.",
    related: [{ path: "/admin/recovery-leads", label: "Leads recovery" }],
  },
  "kimi-patterns": {
    id: "kimi-patterns",
    path: "/admin/kimi-patterns",
    navLabel: "Offer engine",
    title: "Offer engine",
    purpose:
      "Pipeline live DualCTA → cupones ANA* → Stripe paid brand + multi-LLM (primary/fallback/shadow).",
    owns:
      "Live metrics (kimi_decisions/outcomes) + stripe_paid brand + patterns + accept opportunity.",
    notHere:
      "Lista de compradores / reconcile → Compradores. Precios VIP por país → Home por país.",
    related: [
      { path: "/admin/buyers", label: "Compradores" },
      { path: "/admin/country-templates", label: "Home por país" },
      { path: "/admin/checkout-funnel", label: "Funnel checkout" },
      { path: "/admin/suscripciones", label: "Suscripciones" },
    ],
  },
  integrations: {
    id: "integrations",
    path: "/admin/integraciones",
    navLabel: "Integraciones",
    title: "Integraciones",
    purpose: "Health check en vivo: Stripe, MP, DB, Resend, Clerk…",
    owns: "Self-test /dashboard/health.",
    notHere: "Faltantes de env por país → Config Health. Worker edge → Worker.",
    related: [
      { path: "/admin/config-health", label: "Config Health" },
      { path: "/admin/env-setup", label: "Env vars" },
    ],
  },
  "config-health": {
    id: "config-health",
    path: "/admin/config-health",
    navLabel: "Config Health",
    title: "Config Health",
    purpose: "Qué falta configurar por país / template (gaps de producto).",
    owns: "Diagnóstico de config (no edita).",
    notHere: "Ping de APIs → Integraciones. Variables crudas → Env vars.",
    related: [
      { path: "/admin/integraciones", label: "Integraciones" },
      { path: "/admin/country-templates", label: "Home por país" },
    ],
  },
  "worker-flags": {
    id: "worker-flags",
    path: "/admin/worker-flags",
    navLabel: "Worker / Edge",
    title: "Worker / Edge",
    purpose: "Qué inyecta el Cloudflare Worker (__TEMPLATE_CONFIG__) en prod.",
    owns: "Lectura de edge config + docs de vars Worker.",
    notHere: "Overrides DB de CTA → CTAs. Template por país en Postgres → Home por país.",
    related: [
      { path: "/admin/cta", label: "CTAs" },
      { path: "/admin/country-templates", label: "Home por país" },
    ],
  },
  "env-setup": {
    id: "env-setup",
    path: "/admin/env-setup",
    navLabel: "Env vars",
    title: "Variables de entorno",
    purpose: "Checklist de env (Railway / Worker) — deploy-bound.",
    owns: "Documentación y verificación de env (no secretos en claro).",
    related: [
      { path: "/admin/integraciones", label: "Integraciones" },
      { path: "/admin/config-health", label: "Config Health" },
    ],
  },
  "stripe-disputes": {
    id: "stripe-disputes",
    path: "/admin/stripe-disputes",
    navLabel: "Disputas Stripe",
    title: "Disputas Stripe",
    purpose: "Chargebacks abiertos + notas internas.",
    owns: "stripe disputes admin.",
    notHere: "Revenue series → Revenue.",
    related: [{ path: "/admin/revenue", label: "Revenue" }],
  },
  "sidebar-config": {
    id: "sidebar-config",
    path: "/admin/sidebar-config",
    navLabel: "Sidebar",
    title: "Visibilidad del sidebar",
    purpose: "Qué ve el rol admin vs super_admin en el menú.",
    owns: "sidebar visibility config.",
    related: [{ path: "/admin", label: "Dashboard" }],
  },
};

/** Resolve catalog entry from location.pathname. */
export function getAdminPageMeta(pathname: string): AdminPageMeta | null {
  const path = pathname.replace(/\/+$/, "") || "/admin";
  const entries = Object.values(ADMIN_PAGE_CATALOG);
  // Prefer exact match; then longest prefix
  const exact = entries.find((e) => e.path === path);
  if (exact) return exact;
  const sorted = [...entries].sort((a, b) => b.path.length - a.path.length);
  return sorted.find((e) => path === e.path || path.startsWith(e.path + "/")) ?? null;
}

export function getAdminMetaByPath(path: string): AdminPageMeta | null {
  return Object.values(ADMIN_PAGE_CATALOG).find((e) => e.path === path) ?? null;
}
